/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';

import axios from 'axios';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Navigate } from 'react-router-dom';

import { useDataContext } from 'context/UserContext';

import { useTranslation } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

function UpdateInfo({
  currentEmail,
  setCurrentEmail,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [passwordValid, setPasswordValid] = useState(false);

  const reg = /^(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/;

  const { user, updateUser } = useDataContext();

  if (!user.language) {
    const languagedetector = new LanguageDetector();

    let language = languagedetector.detect();

    if (language !== 'es' && !language.includes('en-US')) {
      language = 'en';
    }

    user.language = language;
  }

  const { t } = useTranslation('', {
    lng: user.language || 'en'
  });

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const validateEmail = email => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  const validatePassword = password => {
    const re = /^(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/;
    return re.test(password);
  };

  useEffect(() => {
    if (newPassword === '') {
      setPasswordValid(true);
      return;
    }
    if (reg.test(newPassword)) {
      if (newPassword === confirmPassword) {
        setPasswordValid(true);
      } else {
        setPasswordValid(false);
      }
    } else {
      setPasswordValid(false);
    }
  }, [newPassword, confirmPassword]);

  const onSubmit = () => {
    if (!passwordValid) {
      toast.error(
        t('settings.lengthWarning') + ' ' + t('settings.upperWarning')
      );
      return;
    }

    const body = {
      oldPassword
    };
    if (currentEmail) body.username = currentEmail;
    if (newPassword && newPassword !== oldPassword) body.password = newPassword;

    axios('/user', {
      method: 'POST',
      data: body
    })
      .then(() => {
        updateUser();
        toast.success('Account successfully updated!');
        setConfirmPassword('');
        setNewPassword('');
        setCurrentEmail('');
        return <Navigate to="/dashboard" replace />;
      })
      .catch(e => {
        const error = e?.response?.data?.error;
        toast.error(error ?? 'Something went wrong while updating settings!');
      });
  };

  return (
    <div className="overflow-hidden rounded-md">
      <div className="gradient z-[600] h-1 w-full rounded-t-lg" />
      <div className="p-5 mb-10 bg-white shadow-md">
        <div className="mb-6">
          <div className="text-lg font-medium">
            {t('settings.updateAcctInfo')}
          </div>
          <p className="text-sm">{t('settings.updateSub')}</p>
        </div>
        <div>
          <form
            className="space-y-6"
            onSubmit={e => {
              e.preventDefault();
              const data = Object.fromEntries(new FormData(e.target));
              onSubmit(data);
            }}
          >
            <div className="flex flex-col gap-5 sm:flex-row">
              <div className="w-full">
                <label
                  htmlFor="current-password"
                  className="block text-sm font-medium text-gray-800"
                >
                  {t('settings.currentPassword')}
                </label>
                <div className="w-full mt-1">
                  <input
                    name="oldPassword"
                    type="password"
                    value={oldPassword}
                    placeholder="********"
                    onChange={e => {
                      setOldPassword(e.target.value);
                    }}
                    className="block w-full px-3 py-2 placeholder-gray-400 transition border border-gray-300 rounded-md shadow-sm appearance-none focus:border-highlight focus:outline-none focus:ring-highlight sm:text-sm"
                    id="current-password"
                  />
                </div>
              </div>
              <div className="w-full">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-800"
                >
                  {t('settings.changeEmail')}
                </label>
                <div className="w-full mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={currentEmail}
                    placeholder={user.username}
                    onChange={e => {
                      setCurrentEmail(e.target.value);
                    }}
                    className="block w-full px-3 py-2 placeholder-gray-400 transition border border-gray-300 rounded-md shadow-sm appearance-none focus:border-highlight focus:outline-none focus:ring-highlight sm:text-sm"
                  />
                </div>
                {currentEmail.length > 2 ? (
                  <span className="text-xs font-normal text-rose-500">
                    {!validateEmail(currentEmail) &&
                      'Please enter a valid email address.'}
                  </span>
                ) : null}
              </div>
              <div className="relative w-full">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-800"
                >
                  {t('settings.newPassword')}
                </label>
                <div className="flex items-center mt-1">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('settings.enterNewPassword')}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    autoComplete="current-password"
                    className="block w-full px-3 py-2 text-sm placeholder-gray-400 transition border border-gray-300 rounded-md shadow-sm appearance-none focus:border-highlight focus:outline-none focus:ring-highlight"
                  />
                  <button
                    type="button"
                    className="absolute right-0 px-3 py-1"
                    onClick={toggleShowPassword}
                  >
                    {showPassword ? (
                      <FaEyeSlash className="text-gray-400" />
                    ) : (
                      <FaEye className="text-gray-400" />
                    )}
                  </button>
                </div>
                {newPassword.length > 2 ? (
                  <span className="text-xs font-normal text-rose-500">
                    {newPassword.length < 8 && t('settings.lengthWarning')}
                    {newPassword.length >= 8 &&
                      !validatePassword(newPassword) &&
                      t('settings.upperWarning')}
                  </span>
                ) : null}
                {newPassword.length > 0 ? (
                  <div className="mt-2.5 w-full sm:absolute">
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-800"
                    >
                      {t('settings.confirmPassword')}
                    </label>
                    <div className="mt-1">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        autoComplete="current-password"
                        className="block w-full px-3 py-2 text-sm placeholder-gray-400 transition border border-gray-300 rounded-md shadow-sm appearance-none focus:border-highlight focus:outline-none focus:ring-highlight"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <div>
              <button
                type="submit"
                className={clsx(
                  'button-gradient button-gradient items-center gap-1.5 rounded-md px-4 py-3 text-sm font-medium leading-none tracking-wide text-button-text transition-all lg:inline-flex',
                  passwordValid === false
                    ? 'cursor-not-allowed select-none opacity-50'
                    : 'cursor-pointer opacity-100'
                )}
              >
                {t('settings.updateInfo')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

UpdateInfo.propTypes = {
  currentEmail: PropTypes.string.isRequired,
  setCurrentEmail: PropTypes.func.isRequired,
  newPassword: PropTypes.string.isRequired,
  setNewPassword: PropTypes.func.isRequired,
  confirmPassword: PropTypes.string.isRequired,
  setConfirmPassword: PropTypes.func.isRequired
};

export default UpdateInfo;
